import useTranslation from '../../hooks/useTranslation';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import Badge from 'components/Badge';
import Banner from 'components/Banner';
import EmptyState from 'components/EmptyState';
import Link from 'components/Link';
import PanelSection from 'components/PanelSection';
import RadioButton from 'components/RadioButton';

import { DOCUMENT_LINK_TARGET, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const FormulationDocumentStatements: FunctionComponent<Props> = ({
    className = '',
    document,
    onSelectStatement,
    selectedStatementKey,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const hasStatements = document.statements.length > 0;
    const documentLink = (
        <Link target={DOCUMENT_LINK_TARGET} text={t.OPEN_DOCUMENT} to={document.url} />
    );

    return (
        <PanelSection
            className={`${styles.formulationDocumentStatements} ${className}`}
            rightSlot={documentLink}
            testId={testId}
            text={document.type}>
            {document.error && (
                <Banner metadata={document.error} text={t.DOCUMENT_ERROR} variant='warn' />
            )}

            {!document.error && !hasStatements && <EmptyState text={t.NO_STATEMENTS} />}

            {document.statements.map(option => {
                const isSelected = option.key === selectedStatementKey;
                const selectedClass = isSelected ? styles.selected : '';
                const selectableClass = option.isSelectable ? styles.selectable : '';
                const handleClick = () => onSelectStatement(option.key);

                return (
                    <HorizontalContainer
                        className={`${styles.statement} ${selectableClass} ${selectedClass}`}
                        key={option.key}
                        onClick={option.isSelectable ? handleClick : undefined}
                        testId={`${testId}-statement`}>
                        {option.isSelectable && (
                            <RadioButton className={styles.radioButton} isSelected={isSelected} />
                        )}

                        <VerticalContainer className={styles.statementBody}>
                            <Wrapper className={styles.excerpt}>{option.excerpt}</Wrapper>

                            <HorizontalContainer className={styles.tags}>
                                <Badge
                                    className={styles.codeBadge}
                                    text={option.suggestedCode ?? t.NO_CODE}
                                />

                                {option.selfStatement && (
                                    <Wrapper className={styles.tag}>{t.SELF_STATEMENT}</Wrapper>
                                )}

                                {option.mentionsProduct && (
                                    <Wrapper className={styles.tag}>{t.MENTIONS_PRODUCT}</Wrapper>
                                )}
                            </HorizontalContainer>
                        </VerticalContainer>
                    </HorizontalContainer>
                );
            })}

            {document.mixingOrderStatements.length > 0 && (
                <VerticalContainer className={styles.mixingInstructions}>
                    <Wrapper className={styles.tag}>{t.MIXING_INSTRUCTIONS}</Wrapper>

                    {document.mixingOrderStatements.map(statement => (
                        <Wrapper className={styles.excerpt} key={statement}>
                            {statement}
                        </Wrapper>
                    ))}
                </VerticalContainer>
            )}
        </PanelSection>
    );
};

export default FormulationDocumentStatements;
